import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, Signal, WritableSignal, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { ActivityInterface, GetMultiCrmObjectResponseInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n/translation-module';
import { CrmObjectService } from '../../../shared-services/crm-services/crm-object-service/crm-object.service';
import {
  CrmFieldService,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
} from '../../../shared-services/crm-services/field.service';
import { ObjectType } from '../../../tokens-and-interfaces';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { CrmUnknownUserAvatarComponent } from '../../unknown-user-avatar/unknown-user-avatar.component';
import { ActivityExtended } from '../timeline/timeline.service';
@Component({
  selector: 'crm-activity-call',
  templateUrl: './call.component.html',
  styleUrls: ['./call.component.scss'],
  providers: [RxState],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    CollapsibleSectionComponent,
    MatCardModule,
    GalaxyAvatarModule,
    CrmUnknownUserAvatarComponent,
    GalaxyPipesModule,
  ],
  standalone: true,
})
export class CrmActivityCallComponent {
  dateFormat = DateFormat.mediumDateShortTime;
  callRecordingSource$: Observable<any>;
  private _activity: WritableSignal<ActivityInterface> = signal({});
  private _associatedObjectId: WritableSignal<string> = signal('');
  private _objectType: WritableSignal<ObjectType> = signal('Contact');

  @Input({ required: true }) set activity(activity: ActivityExtended) {
    this._activity.set(activity);
  }
  get activity() {
    return this._activity();
  }
  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }
  get objectType() {
    return this._objectType();
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  get associatedObjectId() {
    return this._associatedObjectId();
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }

  associatedCrmObject$ = toObservable(this._activity).pipe(switchMap(() => this.getAssociatedObjectName()));
  associatedCrmObject = toSignal(this.associatedCrmObject$);

  getAssociatedObjectName(): Observable<string> {
    if (this.objectType === 'Contact') {
      return this.crmObjectService.getMultiObject('Contact', [this.associatedObjectId]).pipe(
        map((data: GetMultiCrmObjectResponseInterface) => {
          const crmObject = data?.crmObjects[0];
          const firstName =
            this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.FirstName)?.stringValue || '';
          const lastName =
            this.fieldService.getFieldValueFromCrmObject(crmObject, StandardExternalIds.LastName)?.stringValue || '';
          return `${firstName} ${lastName}` ?? '';
        }),
      );
    }
    if (this.objectType === 'Company') {
      return this.crmObjectService.getMultiObject('Company', [this.associatedObjectId]).pipe(
        map((data: GetMultiCrmObjectResponseInterface) => {
          const crmObject = data?.crmObjects[0];
          const companyName =
            this.fieldService.getFieldValueFromCrmObject(crmObject, StandardIds.CompanyName)?.stringValue || '';
          return `${companyName}` ?? '';
        }),
      );
    }
    return of('');
  }

  headerText = computed(() => {
    const { direction } = this.cardInfo();
    let directionText = '';
    if (direction === 'Inbound') {
      directionText = `Call from ${this.ownerName()}`;
    } else if (direction === 'Outbound') {
      directionText = `Call to ${this.ownerName()}`;
    }
    return directionText;
  });

  cardTitle = computed(() => {
    const { status, duration } = this.cardInfo();
    const minutes = Math.floor(duration / 60000);
    const seconds = ((duration % 60000) / 1000).toFixed(0);
    if (status === 'Connected') {
      return `Call for ${minutes} minutes, ${seconds} seconds`;
    }
    return `Call - ${status}`;
  });

  ownerName = computed(() => {
    const owner = this.activity?.ownerUser;
    if (owner) {
      return `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim();
    }
    return 'Unknown';
  });

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    const cardInfo: any = {
      status: this.getStringFieldData(this.activity, StandardIds.ActivityCallStatus),
      direction: this.getStringFieldData(this.activity, StandardIds.ActivityCallDirection),
      date:
        this.fieldService.getFieldValueFromCrmObject(this._activity(), SystemFieldIds.ActivityEffectiveDate)
          ?.dateValue ?? null,
      to: this.getStringFieldData(this._activity(), StandardIds.ActivityCallToNumber),
      from: this.getStringFieldData(this._activity(), StandardIds.ActivityCallFromNumber),
      duration:
        this.fieldService.getFieldValueFromCrmObject(this._activity(), StandardIds.ActivityCallDuration)
          ?.integerValue ?? 0,
    };
    const callRecordingUrl = this.getStringFieldData(this._activity(), StandardIds.ActivityCallRecordingUrl);
    if (callRecordingUrl) {
      cardInfo.callRecordingUrl = callRecordingUrl;
    }
    return cardInfo;
  });

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly http: HttpClient,
    private readonly crmObjectService: CrmObjectService,
  ) {
    this.callRecordingSource$ = toObservable(this.cardInfo).pipe(
      switchMap((cardInfo) => {
        if (!cardInfo.callRecordingUrl) {
          return of(null);
        }
        return this.http.get(cardInfo.callRecordingUrl, {
          responseType: 'text',
          withCredentials: true,
        });
      }),
    );
  }

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;
}

interface ActivityCardInformation {
  status: string;
  direction: string;
  callRecordingUrl?: string;
  date: Date;
  to: string;
  from: string;
  duration: number;
}
